import { Controller } from "@hotwired/stimulus"

// Dokumentenlayout settings (Einstellungen -> Abrechnung). Keeps each pair of inputs in step
// (the colour swatch and its hex field, the Logo-Größe slider and its number field) and
// resets colour, position and size to the schema defaults. Only the swatch, the radios and
// the slider carry a name; the hex and number fields are just another way to type the value.
//
// Every programmatic change is announced with a bubbling "input" event: that is what the
// sibling billing-document-preview controller listens for on the form to re-render, and it
// also runs this controller's own sync actions, so the paired field follows.
const HEX = /^#?([0-9a-f]{6})$/i

export default class extends Controller {
  static targets = ["color", "hex", "position", "scale", "scaleNumber"]
  static values = { defaults: Object }

  colorChanged() {
    this.hexTarget.value = this.colorTarget.value.toUpperCase()
    this.hexTarget.removeAttribute("aria-invalid")
  }

  // Applied only once the hex is complete; a half-typed value is flagged, never submitted.
  hexChanged() {
    const match = this.hexTarget.value.trim().match(HEX)
    this.hexTarget.toggleAttribute("aria-invalid", !match)
    if (match) this.setColor(`#${match[1].toLowerCase()}`)
  }

  hexBlurred() {
    this.colorChanged()
  }

  pickPreset(event) {
    this.setColor(event.currentTarget.dataset.color)
  }

  scaleChanged() {
    this.scaleNumberTarget.value = this.scaleTarget.value
  }

  scaleNumberChanged() {
    const value = Number(this.scaleNumberTarget.value)
    const min = Number(this.scaleTarget.min)
    const max = Number(this.scaleTarget.max)
    if (!Number.isInteger(value) || value < min || value > max) return

    this.scaleTarget.value = value
    this.announce(this.scaleTarget)
  }

  // An out-of-range number was never applied; show the slider's value again.
  scaleNumberBlurred() {
    this.scaleChanged()
  }

  // Form values only: like every other field on the tab it takes effect on "Speichern".
  reset() {
    const defaults = this.defaultsValue
    this.setColor(defaults.billing_pdf_accent_color)

    for (const radio of this.positionTargets) {
      radio.checked = radio.value === defaults.billing_pdf_logo_position
      if (radio.checked) this.announce(radio)
    }

    this.scaleTarget.value = defaults.billing_pdf_logo_scale
    this.announce(this.scaleTarget)
  }

  setColor(hex) {
    this.colorTarget.value = hex.toLowerCase()
    this.announce(this.colorTarget)
  }

  announce(element) {
    element.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
